/** Applies and resolves knockback physics for entities, including wall collision and enemy-to-enemy stagger. */
import Phaser from 'phaser';
import { Entity } from '../entities/Entity';
import { Enemy } from '../entities/Enemy';
import { Player } from '../entities/Player';
import { PhysicsComponent } from '../components/PhysicsComponent';
import { HealthComponent } from '../components/HealthComponent';
import { AIComponent } from '../components/AIComponent';
import { ConfigLoader } from './ConfigLoader';
import type { AttackType } from '../components/CombatComponent';
import type { Position } from '../utils/Types';
import { distance } from '../utils/Math';
import { DebugLog } from '../debug/DebugLog';
import { DebugConfig } from '../debug/DebugConfig';

interface KnockbackEntry {
  entity: Entity;
  direction: Position;
  speed: number;
  remaining: number;
  attackType: AttackType;
}

/** Applies directional knockback velocity to entities and resolves wall and inter-enemy collisions. */
export class KnockbackSystem {
  private scene: Phaser.Scene;
  private groundLayer: Phaser.Tilemaps.TilemapLayer;
  private active: KnockbackEntry[] = [];
  private enemies: () => readonly Enemy[];

  constructor(
    scene: Phaser.Scene,
    groundLayer: Phaser.Tilemaps.TilemapLayer,
    enemiesAccessor: () => readonly Enemy[]
  ) {
    this.scene = scene;
    this.groundLayer = groundLayer;
    this.enemies = enemiesAccessor;
  }

  /** Launch an entity in the given direction with speed and duration derived from the attack type config. */
  apply(entity: Entity, attackType: AttackType, direction: Position): void {
    const config = ConfigLoader.combat.knockback;
    const multiplier = ConfigLoader.getKnockbackMultiplier(attackType);
    const baseDist = config.baseDistance * multiplier;

    // Check knockback resistance and player multiplier
    let resistance = 0;
    let targetMult = 1;
    if (entity instanceof Enemy) {
      resistance = entity.config.knockbackResistance ?? 0;
      if (entity.config.weight === 'immovable') return;
      entity.isKnockedBack = true;
    } else if (entity instanceof Player) {
      targetMult = config.playerKnockbackMultiplier ?? 0.5;
    }

    const dist = baseDist * (1 - resistance) * targetMult;
    if (dist <= 0) return;

    const speed = dist * config.velocityMultiplier;
    const duration = config.durationMs;

    // Set velocity
    const physics = entity.getComponent<PhysicsComponent>('physics');
    if (physics) {
      physics.body.setVelocity(
        direction.x * speed,
        direction.y * speed
      );
    }

    const isPlayer = entity instanceof Player;
    if (isPlayer) {
      const s = entity.sprite;
      DebugLog.log('combat', `KNOCKBACK player at (${s.x.toFixed(0)},${s.y.toFixed(0)}) dir=(${direction.x.toFixed(2)},${direction.y.toFixed(2)}) dist=${dist.toFixed(1)} speed=${speed.toFixed(0)} type=${attackType}`);
    }

    this.active.push({
      entity,
      direction,
      speed,
      remaining: duration,
      attackType,
    });
  }

  /** Launch an enemy in a given direction with explicit speed and duration — ignores weight and resistance. */
  applyRaw(entity: Enemy, direction: Position, speed: number, durationMs: number): void {
    entity.isKnockedBack = true;

    // Stagger the enemy for the knockback duration so AI doesn't fight the velocity
    const ai = entity.getComponent<AIComponent>('ai');
    if (ai) ai.applyStagger(durationMs, true);

    const physics = entity.getComponent<PhysicsComponent>('physics');
    if (physics) {
      physics.body.setVelocity(direction.x * speed, direction.y * speed);
    }

    this.active.push({
      entity,
      direction,
      speed,
      remaining: durationMs,
      attackType: 'shockwave',
    });
  }

  /** Advance all active knockback entries, checking collisions and expiring finished entries. */
  update(delta: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const entry = this.active[i];
      entry.remaining -= delta;

      // Shockwave entries slide along walls instead of stopping
      if (entry.attackType !== 'shockwave') {
        this.checkWallCollision(entry);
        this.checkEnemyCollision(entry);
      }

      if (entry.remaining <= 0) {
        // Clear knockback state
        if (entry.entity instanceof Enemy) {
          entry.entity.isKnockedBack = false;
        }
        const physics = entry.entity.getComponent<PhysicsComponent>('physics');
        if (physics) {
          physics.body.setVelocity(0, 0);
        }
        this.active.splice(i, 1);
      }
    }
  }

  private checkWallCollision(entry: KnockbackEntry): void {
    const sprite = entry.entity.sprite;
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    // Check if the entity is touching/blocked by a wall tile
    if (body.blocked.left || body.blocked.right || body.blocked.up || body.blocked.down) {
      const config = ConfigLoader.combat.knockback;
      const isPlayer = entry.entity instanceof Player;

      if (isPlayer) {
        const dirs = [body.blocked.left && 'L', body.blocked.right && 'R', body.blocked.up && 'U', body.blocked.down && 'D'].filter(Boolean).join('');
        DebugLog.log('combat', `WALL-HIT player at (${sprite.x.toFixed(0)},${sprite.y.toFixed(0)}) blocked=${dirs} vel=(${body.velocity.x.toFixed(0)},${body.velocity.y.toFixed(0)}) from=${entry.attackType}`);
      }

      // Wall bonus damage (skip if player has god mode)
      const godMode = isPlayer && DebugConfig.flags.playerGodMode;
      if (!godMode) {
        const health = entry.entity.getComponent<HealthComponent>('health');
        if (health && !health.isDead) {
          let wallDamage = Math.round(config.baseDistance * config.wallBonusDamageMultiplier * 0.2);
          if (entry.entity instanceof Enemy) {
            wallDamage = Math.round(wallDamage * entry.entity.getVulnerabilityMultiplier());
          }
          health.takeDamage(wallDamage);
        }
      }

      // End knockback early on wall hit
      entry.remaining = 0;
    }
  }

  private checkEnemyCollision(entry: KnockbackEntry): void {
    if (!(entry.entity instanceof Enemy)) return;

    const entBody = entry.entity.sprite.body as Phaser.Physics.Arcade.Body;
    if (!entBody) return;

    const config = ConfigLoader.combat.knockback;

    for (const other of this.enemies()) {
      if (other === entry.entity) continue;

      const otherBody = other.sprite.body as Phaser.Physics.Arcade.Body;
      if (!otherBody) continue;

      // Simple distance check for collision
      const dist = distance(entry.entity.sprite.x, entry.entity.sprite.y, other.sprite.x, other.sprite.y);

      if (dist < config.enemyCollisionDistance) {
        // Stagger the secondary target
        const ai = other.getComponent<AIComponent>('ai');
        if (ai) {
          ai.applyStagger(config.enemyCollisionStaggerMs, true);
        }
      }
    }
  }

  /** Return true if the entity is currently under an active knockback effect. */
  isKnockedBack(entity: Entity): boolean {
    return this.active.some(e => e.entity === entity);
  }

  /** Cancel all active knockback entries and reset entity knockback flags. */
  clear(): void {
    for (const entry of this.active) {
      if (entry.entity instanceof Enemy) {
        entry.entity.isKnockedBack = false;
      }
    }
    this.active = [];
  }
}
